import { AssetManager } from 'components/sharpr/assetManager/AssetManager';

let apiPromise;

const initApi = () => {
	if (!apiPromise) {
		apiPromise = new Promise((resolve, reject) => {
			AssetManager.script('https://www.youtube.com/iframe_api').then(() => {}, reject);
			window.onYouTubeIframeAPIReady = resolve;
		});
	}
	return apiPromise;
};

/**
 * Create and manage iframes to control YouTube videos
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
export class VideoPlayerYoutube {
	/**
	 * @param {Element} element  The element to which to append the YouTube iframe
	 */
	constructor(element) {
		/**
		 * The element to which to append the YouTube iframe
		 * @type {Element}
		 */
		this.element = element;
		// TODO: use this.timeout to handle videos that are forbidden to be embedded or removed by copyright holder
		/**
		 * The number of milliseconds after which to consider playback failed
		 * @type {Number}
		 */
		this.timeout = 5000;
		/**
		 * A number between 0 and 1 where 0 is muted and 1 is full folume
		 * @type {Number}
		 */
		this.volume = 1;
		/**
		 * The type of media player ("youtube")
		 * @type {String}
		 */
		this.type = 'youtube';
	}

	/**
	 * Extract the YouTube video ID from the given URL
	 * @param {String} url
	 * @returns {String|null}
	 */
	static extractVideoId(url) {
		let regexes = [/[?&]v=([\w_-]+)/, /\/v\/([\w_-]+)/, /\/embed\/([\w_-]+)/, /^https?:\/\/youtu\.be\/([\w_-]+)/];
		for (let i = 0; i < regexes.length; i++) {
			let match = url.match(regexes[i]);
			if (match) {
				return match[1];
			}
		}
		return null;
	}

	/**
	 * Determine if the given URL is playable
	 * @param {String} url
	 * @returns {Boolean}
	 */
	static isPlayable(url) {
		const matcher = /^https?:\/\/(([a-z.-]+\.)?youtube\.com|youtu\.be\/)/i;
		return matcher.test(url) && !!this.extractVideoId(url);
	}

	/**
	 * Attempt to play the given URL
	 * @param {String} url
	 * @returns {Promise} Resolved when video is complete; rejected if playback errors
	 */
	play(url) {
		return new Promise((resolve, reject) => {
			/**
			 * The URL of the currently-playing video
			 * @type {String}
			 */
			this.url = url;
			if (document.location.protocol == 'file:') {
				this._initIframeEmbed();
			} else {
				this._onVideoEnded = resolve;
				this._onVideoError = reject;
				initApi().then(this._initPlayer.bind(this));
			}
		});
	}

	/**
	 * Suspend playback
	 */
	pause() {
		if (this.player) {
			this.player.pauseVideo();
		}
	}

	/**
	 * Stop playback and remove iframe
	 */
	stop() {
		// YT player api does more than expected on this.player.stop()
		this.pause();
		this._cleanup();
	}

	/**
	 * Set the volume where 0 is muted and 1 is full volume
	 * @param {Number} volume
	 */
	setVolume(volume) {
		if (typeof volume == 'number' && volume >= 0 && volume <= 1) {
			this.volume = volume;
		}
		if (this.player && typeof this.player.setVolume == 'function') {
			// in some race conditions this.player.setVolume is not yet a function
			// also in cordova, we have no player, just the iframe embed
			this.player.setVolume(Math.ceil(this.volume * 100));
		}
	}

	_initIframeEmbed() {
		console.log('Embeding youtube video ' + this.url);
		let url, html;
		try {
			url = `https://www.youtube.com/embed/${VideoPlayerYoutube.extractVideoId(this.url)}?autoplay=1`;
			html = `<iframe src="${url}" frameborder="0" style="width:100%;height:100%"></iframe>`;
			this.element.innerHTML = html;
		} catch (e) {
			console.log(`Exception creating iframe using innerHTML ${html} with message ${e.message}`);
		}
		console.log('Embeded youtube iframe ok: ' + url);
	}

	/**
	 * Initialize the player once <script> loads
	 * @private
	 */
	_initPlayer() {
		// console.log('_initPlayer');
		if (this.element.firstChild) {
			// remove old iframe if needed
			this.element.removeChild(this.element.firstChild);
		}
		let replaceDiv = document.createElement('div');
		this.element.appendChild(replaceDiv);
		/** @private */
		this.player = new window.YT.Player(replaceDiv, {
			events: {
				onReady: this._startVideo.bind(this),
				onStateChange: this._checkForVideoEnd.bind(this),
			},
		});
	}

	/**
	 * Remove the iframe container
	 * @private
	 */
	_cleanup() {
		try {
			this.player = null;
			this.element.removeChild(this.element.firstChild);
		} catch (e) {}
	}

	/**
	 * Handle state events and call _onVideoEnded callback when player state is ended
	 * @param {Event} evt
	 * @private
	 */
	_checkForVideoEnd(evt) {
		if (evt.data == window.YT.PlayerState.ENDED) {
			this._onVideoEnded();
		}
	}

	/**
	 * Attempt to start the video with this.url
	 * @private
	 */
	_startVideo() {
		this.player.loadVideoById(VideoPlayerYoutube.extractVideoId(this.url));
		if (typeof this.volume == 'number') {
			this.setVolume(this.volume);
		}
	}
}
